import React, { useEffect, useState, useRef, useMemo } from 'react'; 
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Box,
  Typography,
  Divider,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
  Grid,
  Paper,
  Grow,
} from '@mui/material';
import PropTypes from 'prop-types';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { styled, keyframes } from '@mui/system';
import {
  createUser,
  getOrganizations,
  updateUser,
  getUserById,
  getOrganizationById,
  updateUserTrialConfig,
  extendUserTrial,
} from '../apis/Admin';
import { toast } from 'react-toastify';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import Cookies from 'js-cookie';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const underlineGrow = keyframes`
  from { transform: scaleX(0); opacity: .2; }
  to   { transform: scaleX(1); opacity: 1; }
`;

const softPulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(25,118,210,0.24); }
  70%  { box-shadow: 0 0 0 8px rgba(25,118,210,0.04); }
  100% { box-shadow: 0 0 0 0 rgba(25,118,210,0.0); }
`;

const TitleWrap = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  animation: `${fadeUp} 260ms ease-out both`,
  color: '#5965A6', // theme works here
}));

const IconBadge = styled('div')(() => ({
  width: 36,
  height: 36,
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background:
    'linear-gradient(180deg, rgba(25,118,210,0.12), rgba(25,118,210,0.06))',
  border: `1px solid #6e7cd0`,
  animation: `${softPulse} 2.2s ease-out infinite`,
}));

const TitleText = styled('div')(() => ({
  fontSize: '1.05rem',
  fontWeight: 700,
  color: '#000',
  letterSpacing: 0.2,
  lineHeight: 1.2,
}));

const TitleUnderline = styled('div')(() => ({
  height: 5,
  width: '100%',
  transformOrigin: 'left',
  transform: 'scaleX(1)',
  marginTop: 6,
  background: `linear-gradient(90deg, #5965A6, transparent)`,
  animation: `${underlineGrow} 480ms 120ms ease-out both`,
  opacity: 0.85,
}));

export const StyledTextField = styled(TextField)(({ multiline }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    transition: 'border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    height: multiline ? 85 : 45,
    alignItems: 'flex-start',
    '& fieldset': { border: '0.5px solid #e5e7eb' },
    '&:hover fieldset': { border: '0.5px solid #d1d5db' },
    '&.Mui-focused fieldset': { border: '0.6px solid #5965A6' },
  },
  '& .MuiInputBase-input': {
    padding: multiline ? '8px 10px' : '10px 14px',
    fontSize: '0.9rem',
    fontWeight: 400,
    color: '#374151',
  },
  '& .MuiInputLabel-root': {
    color: '#5965A6',
    fontSize: '0.875rem',
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#5965A6' },
  '& .MuiInputLabel-root.Mui-error': { color: '#d32f2f' },
}));

const empty = {
  id: '',
  first_name: '',
  last_name: '',
  username: '',
  organizationid: '',
  email: '',
  role: 'User',
  status: 'Active',
  password: '',
};

export default function UserDialog({
  open,
  mode = 'create',
  user,
  onClose,
  onSubmit,
}) {
  const rawRole = Cookies.get('Role_id') ?? Cookies.get('role_id');

  const rawOrgId = Cookies.get('Org_id') ?? Cookies.get('org_id');

  console.log('rawRole:', rawRole, 'rawOrgId:', rawOrgId);

  const isAdmin = useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && n === 2; // 2 = Admin
  }, [rawRole]);
  const [form, setForm] = useState(empty);

  // step handling
  const [step, setStep] = useState('user');
  const [orgInfo, setOrgInfo] = useState({ org_id: '', name: '', details: '' });
  //eslint-disable-next-line
  const [orgCreated, setOrgCreated] = useState(false);

  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ next: '', confirm: '' });
  const [show, setShow] = useState({ next: false, confirm: false });

  const [errors, setErrors] = useState({});

  // organizations list for dropdown
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [freshUserData, setFreshUserData] = useState(null);
  const orgResolvedRef = useRef(false);
  
  const [trialSettings, setTrialSettings] = useState({
    trial_days: 14,
    max_connectors: 5,
    days_limit_enabled: true,
    connectors_limit_enabled: true,
    trial_start_date: null,
    trial_end_date: null,
  });
  const [trialExtensionDays, setTrialExtensionDays] = useState('');
  const [extendingTrial, setExtendingTrial] = useState(false);
  
  // Seat info for selected organization (Super Admin only)
  const [selectedOrgSeatInfo, setSelectedOrgSeatInfo] = useState(null);
  const [loadingSeatInfo, setLoadingSeatInfo] = useState(false);
  
  // Check if Super Admin
  const isSuperAdmin = useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && n === 1;
  }, [rawRole]);

  // Reset seat info when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedOrgSeatInfo(null);
    }
  }, [open]);

  // reset on open
  useEffect(() => {
    if (!open) {
      setFreshUserData(null);
      orgResolvedRef.current = false;
      setSelectedOrgSeatInfo(null);
      return;
    }
    orgResolvedRef.current = false;

    if (mode === 'edit' && user) {
      // Always start with empty form to prevent showing previous user's data
      const seeded = {
        ...empty,
        // Explicitly set each field, using empty string if null/undefined
        id: user.id || user.user_id || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        username: user.username || '',
        email: user.email || '',
        organizationid: String(
          user.org_id || user.organizationid || user.orgId || '',
        ),
        role: isAdmin ? 'User' : user.role || user.role_name || empty.role,
        status: user.is_active === false ? 'Inactive' : 'Active',
        password: '',
      };

      // If name is provided as a single field, split it
      if (!seeded.first_name && !seeded.last_name && user.name) {
        const parts = String(user.name).trim().split(/\s+/);
        seeded.first_name = parts[0] || '';
        seeded.last_name = parts.slice(1).join(' ') || '';
      }

      if (user.is_active !== undefined) {
        seeded.status = user.is_active ? 'Active' : 'Inactive';
      }
      if (user.role_name && !seeded.role) {
        seeded.role = user.role_name === 'Admin' ? 'Admin' : 'User';
      }
      if (user.org_id && !seeded.organizationid) {
        seeded.organizationid = String(user.org_id);
      }

      setForm(seeded);
      setPwOpen(false);
      setPw({ next: '', confirm: '' });
      setErrors({});
      setStep('user'); // edit: go straight to user form
    }
    else {
      const orgIdFromCookie = String(rawOrgId ?? '').trim();

      const seededCreate = {
        ...empty,
        organizationid: isAdmin && orgIdFromCookie ? orgIdFromCookie : '',
        role: isAdmin ? 'User' : empty.role,
      };

      setForm(seededCreate);
      setPwOpen(false);
      setPw({ next: '', confirm: '' });
      setErrors({});
      // ✅ For Admin, go straight to Add User step
      setStep('user');
      setOrgInfo({ org_id: '', name: '', details: '' });
    }
    // eslint-disable-next-line
  }, [open, mode, user?.id, user?.user_id]);

  useEffect(() => {
    if (!open || mode !== 'edit') return;

    const uid = user?.user_id ?? user?.id;
    if (!uid) return;

    (async () => {
      try {
        const fresh = await getUserById(uid);
        setFreshUserData(fresh);

        // Always start with empty form to prevent showing previous user's data
        const seeded = {
          ...empty,
          // Explicitly set each field, using empty string if null/undefined
          id: fresh.id || fresh.user_id || '',
          first_name: fresh.first_name || '',
          last_name: fresh.last_name || '',
          username: fresh.username || '',
          email: fresh.email || '',
          organizationid: String(
            fresh.org_id ?? fresh.organizationid ?? fresh.orgId ?? '',
          ).trim(),
          role: fresh.role ?? fresh.role_name ?? 'User',
          status: fresh.is_active === false ? 'Inactive' : 'Active',
          password: '', // Never show password
        };

        // If name is provided as a single field, split it
        if (!seeded.first_name && !seeded.last_name && fresh.name) {
          const parts = String(fresh.name || '')
            .trim()
            .split(/\s+/);
          seeded.first_name = parts[0] || '';
          seeded.last_name = parts.slice(1).join(' ') || '';
        }

        setForm(seeded);
        setStep('user');
        
        if (isSuperAdmin && fresh.trial_days !== undefined) {
          setTrialSettings({
            trial_days: fresh.trial_days || 14,
            max_connectors: fresh.max_connectors || 5,
            days_limit_enabled: fresh.days_limit_enabled !== false,
            connectors_limit_enabled: fresh.connectors_limit_enabled !== false,
            trial_start_date: fresh.trial_start_date || null,
            trial_end_date: fresh.trial_end_date || null,
          });
        }
      } catch (err) {
        console.error('Failed to fetch user by id:', err);
        toast.error(err?.detail || 'Failed to load user');
      }
    })();
  }, [open, mode, user?.user_id, user?.id, isSuperAdmin]);

  useEffect(() => {
    if (!open || mode !== 'edit' || !organizations.length) return;

    const userData = freshUserData || user;
    if (!userData) {
      orgResolvedRef.current = true;
      return;
    }

    setForm((f) => {
      const currentOrgId = String(f.organizationid || '').trim();

      if (currentOrgId) {
        const hasValidOrg = organizations.some(
          (o) =>
            String(o.org_id || o.orgId || o.id || '').trim() === currentOrgId,
        );
        if (hasValidOrg && orgResolvedRef.current) {
          return f;
        }
      }

      let resolved =
        userData.org_id ?? userData.organizationid ?? userData.orgId ?? '';

      if (!resolved && userData.org_name && organizations.length) {
        const match = organizations.find(
          (o) =>
            (o.org_name || o.name || '').toLowerCase() ===
            String(userData.org_name || '').toLowerCase(),
        );
        if (match) {
          resolved = match.org_id ?? match.orgId ?? match.id ?? '';
        }
      }

      if (!resolved && userData.user_id && organizations.length) {
        const parent = organizations.find(
          (o) =>
            Array.isArray(o.users) &&
            o.users.some(
              (u) =>
                String(u.user_id || u.id) ===
                String(userData.user_id || userData.id),
            ),
        );
        if (parent) {
          resolved = parent.org_id ?? parent.orgId ?? parent.id ?? '';
        }
      }

      // Only update if we found something and it's different from current
      if (resolved) {
        const resolvedStr = String(resolved).trim();
        if (resolvedStr !== currentOrgId) {
          orgResolvedRef.current = true;
          return { ...f, organizationid: resolvedStr };
        }
      }

      if (!orgResolvedRef.current) {
        orgResolvedRef.current = true;
      }

      return f;
    });
  }, [open, mode, organizations.length, freshUserData, user]);

  useEffect(() => {
    if (!open) return;

    const fetchOrgs = async () => {
      setLoadingOrgs(true);
      try {
        const orgs = await getOrganizations();
        setOrganizations(orgs);
      } catch (error) {
        console.error('Failed to load organizations:', error);
        toast.error(
          (typeof error?.response?.data === 'string' && error.response.data) ||
            error?.response?.data?.detail ||
            error?.response?.data?.message ||
            error?.message ||
            'Request failed',
        );
      } finally {
        setLoadingOrgs(false);
      }
    };

    fetchOrgs();
  }, [open]);

  const emailOk = (v) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim());


  const showOrgSummary = step === 'user' && Boolean(orgInfo?.org_id);

  // ---------- USER STEP ----------
  const validatePwEdit = () => {
    const e = {};
    if (pwOpen) {
      const next = pw.next?.trim() ?? '';
      const confirm = pw.confirm?.trim() ?? '';
      if (!next) e.next = 'New password is required';
      else if (next.length < 8) e.next = 'Minimum 8 characters';
      if (!confirm) e.confirm = 'Confirm your new password';
      else if (confirm !== next) e.confirm = 'Passwords do not match';
    }
    setErrors((prev) => {
      const out = { ...prev, next: e.next, confirm: e.confirm };
      if (!e.next) delete out.next;
      if (!e.confirm) delete out.confirm;
      return out;
    });
    return Object.keys(e).length === 0;
  };

  const validateForm = () => {
    const e = {};
    if (!form.first_name?.trim()) e.first_name = 'First name is required';
    if (!form.last_name?.trim()) e.last_name = 'Last name is required';

    if (mode === 'create' && !form.username?.trim()) {
      e.username = 'Username is required';
    }

    if (mode === 'create' && !form.organizationid?.trim()) {
      e.organizationid = 'Organization is required';
    }

    if (!form.email?.trim()) e.email = 'Email is required';
    else if (!emailOk(form.email)) e.email = 'Enter a valid email';

    if (mode === 'edit' && pwOpen) {
      if (!pw.next) e.next = 'New password is required';
      else if (pw.next.length < 8) e.next = 'Minimum 8 characters';
      if (!pw.confirm) e.confirm = 'Confirm your new password';
      else if (pw.confirm?.trim() !== pw.next?.trim())
        e.confirm = 'Passwords do not match';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validateForm()) return;

    const role_id = isAdmin
      ? 3 //  isAdmin can only create/edit as User
      : form.role === 'Admin'
        ? 2
        : 3;

    // helper: send null when org is empty (often what backends expect)
    const toNullable = (v) => {
      const s = String(v ?? '').trim();
      return s.length ? s : null;
    };

    const safeTrim = (v) => {
      return v ? String(v).trim() : '';
    };

    const base = {
      first_name: safeTrim(form.first_name),
      last_name: safeTrim(form.last_name),
      email: safeTrim(form.email),
      role_id,
      username: safeTrim(form.username),
    };

    try {
      let res;

      if (mode === 'edit') {
        const userId = user?.user_id ?? user?.id ?? form.id;
        if (!userId) {
          toast.error('Missing user id');
          return;
        }

        const userData = freshUserData || user;
        const hasOriginalOrg = userData?.org_name && String(userData.org_name).trim() !== '';
        
        const body = {
          ...base,
          is_active: form.status === 'Active',
        };

        if (!hasOriginalOrg && form.organizationid?.trim()) {
          body.org_id = toNullable(form.organizationid);
        }

        // ✅ only when "Change password" is checked
        if (pwOpen) {
          body.new_password = String(pw.next || '');
          body.confirm_password = String(pw.confirm || '');
        }

        res = await updateUser(userId, body);
        
        if (isSuperAdmin && !form.organizationid) {
          try {
            await updateUserTrialConfig(userId, {
              trial_days: trialSettings.trial_days,
              max_connectors: trialSettings.max_connectors,
              days_limit_enabled: trialSettings.days_limit_enabled,
              connectors_limit_enabled: trialSettings.connectors_limit_enabled,
            });
          } catch (trialErr) {
            console.error('Failed to update trial config:', trialErr);
            toast.warning('User updated but trial config failed to save');
          }
        }
        
        toast.success('User updated successfully!');
        onSubmit(res);
        onClose();
      } else {
        // ✅ create mode – no password in payload
        const body = {
          ...base,
          is_active: true,
          org_id: toNullable(form.organizationid),
        };
        res = await createUser(body);
        toast.success('User created successfully!');
        onSubmit(res);
        onClose();
      }
    } catch (error) {
      console.error('Failed to save user:', error);
      toast.error(
        (typeof error?.response?.data === 'string' && error.response.data) ||
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to save user',
      );
    }
  };

  const title =
    step === 'org'
      ? 'Setup Organization'
      : mode === 'edit'
        ? 'Edit User'
        : 'Add New User';
  const cta = mode === 'edit' ? 'Save Changes' : 'Add User';

  const adorn = (k) => ({
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          onClick={() => setShow((s) => ({ ...s, [k]: !s[k] }))}
          edge="end"
          aria-label="toggle password visibility"
        >
          {show[k] ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  });

  const getOrgName = (list, id) => {
    const norm = (v) =>
      String(v ?? '')
        .trim()
        .toLowerCase();
    const o = list.find(
      (x) => norm(x?.org_id ?? x?.orgId ?? x?.id) === norm(id),
    );
    return o?.org_name || o?.name || '';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: (theme) => ({
          border: `1px solid #6e7cd0}`,
          boxShadow: '0 6px 24px rgba(25,118,210,0.25)',
          backgroundColor: theme.palette.background.paper, // ✅ no undefined
        }),
      }}
    >
      <DialogTitle sx={{ p: '16px 24px 8px' }}>
        <TitleWrap>
          <IconBadge>
            {step === 'org' ? (
              <BusinessRoundedIcon sx={{ color: '#5965A6', fontSize: 20 }} />
            ) : (
              <PersonAddAlt1RoundedIcon
                sx={{ color: '#5965A6', fontSize: 20 }}
              />
            )}
          </IconBadge>

          <Box sx={{ minWidth: 0 }}>
            <TitleText>{title}</TitleText>
            <TitleUnderline />
          </Box>
        </TitleWrap>
      </DialogTitle>

      <DialogContent>
        {/* STEP 1: ORG FORM */}
        {/* {step === 'org' && (
          <Box sx={{ mt: 3, height: '200px', overflowY: 'auto', p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <StyledTextField
                  label="Organization Name"
                  value={orgForm.name}
                  onChange={(e) =>
                    setOrgForm((o) => ({ ...o, name: e.target.value }))
                  }
                  fullWidth
                  error={!!orgErrors.name}
                  helperText={orgErrors.name || ' '}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  label="Organization Details"
                  value={orgForm.details}
                  onChange={(e) =>
                    setOrgForm((o) => ({ ...o, details: e.target.value }))
                  }
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Grid>
            </Grid>
          </Box>
        )} */}

        {/* STEP 2: USER FORM */}
        {step === 'user' && (
          <Box sx={{ mt: 1 }}>
            {/* Small summary card (org info) */}

            <Grow in={showOrgSummary} mountOnEnter unmountOnExit>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  mb: 2,
                  borderRadius: 1,
                  bgcolor: '#F8FAFF',
                  borderColor: '#E5E7EB',
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 0.5 }}>
                  Organization
                </Typography>
                <Typography sx={{ fontSize: 13 }}>
                  <strong>Name:</strong> {orgInfo.name}
                </Typography>
                <Typography sx={{ fontSize: 13 }}>
                  <strong>Details:</strong> {orgInfo.details || '—'}
                </Typography>
                <Typography sx={{ fontSize: 12, mt: 0.5, opacity: 0.8 }}>
                  <strong>ID:</strong> {orgInfo.org_id}
                </Typography>
              </Paper>
            </Grow>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  name="first_name"
                  label="First Name"
                  value={form.first_name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, first_name: e.target.value }))
                  }
                  variant="outlined"
                  fullWidth
                  error={!!errors.first_name}
                  helperText={errors.first_name || ' '}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  name="last_name"
                  label="Last Name"
                  value={form.last_name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, last_name: e.target.value }))
                  }
                  variant="outlined"
                  fullWidth
                  error={!!errors.last_name}
                  helperText={errors.last_name || ' '}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                {(isAdmin && ( mode === 'create'|| mode === 'edit'))  ? (
                  // ✅ Admin: org comes from cookie, read-only, no dropdown
                  <StyledTextField
                    name="organizationid"
                    label="Organization"
                    value={
                      getOrgName(organizations, form.organizationid) ||
                      String(form.organizationid ?? '')
                    }
                    variant="outlined"
                    fullWidth
                    disabled
                    helperText={' '}
                  />
                ) : (
                  // ✅ Existing behavior for others (non-admin / edit)
                  <>
                    <StyledTextField
                      select
                      name="organizationid"
                      label="Organization"
                      value={String(form.organizationid ?? '')}
                      onChange={async (e) => {
                        const orgId = String(e.target.value ?? '').trim();
                        setForm((f) => ({
                          ...f,
                          organizationid: orgId,
                        }));
                        
                        // Fetch seat info for Super Admin when creating user
                        if (isSuperAdmin && mode === 'create' && orgId) {
                          setLoadingSeatInfo(true);
                          try {
                            const orgData = await getOrganizationById(orgId);
                            setSelectedOrgSeatInfo({
                              total_seats: orgData.total_seats || 0,
                              used_seats: orgData.used_seats || 0,
                              available_seats: orgData.available_seats !== undefined 
                                ? orgData.available_seats 
                                : orgData.total_seats === 0 
                                  ? -1 
                                  : (orgData.total_seats || 0) - (orgData.used_seats || 0),
                            });
                          } catch (error) {
                            console.error('Failed to fetch organization seat info:', error);
                            setSelectedOrgSeatInfo(null);
                          } finally {
                            setLoadingSeatInfo(false);
                          }
                        } else {
                          setSelectedOrgSeatInfo(null);
                        }
                      }}
                      variant="outlined"
                      fullWidth
                      error={!!errors.organizationid}
                      helperText={errors.organizationid || ' '}
                      disabled={loadingOrgs}
                      SelectProps={{
                        renderValue: (selected) => {
                          const id = String(selected ?? '').trim();
                          if (!id) return '';
                          return getOrgName(organizations, id) || id;
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        Select
                      </MenuItem>
                      {organizations.map((org) => (
                        <MenuItem
                          key={org.org_id || org.id}
                          value={String(org.org_id || org.id).trim()}
                        >
                          {org.org_name || org.name || 'Unnamed Organization'}
                        </MenuItem>
                      ))}
                    </StyledTextField>
                    
                    {/* Seat Availability Info for Super Admin */}
                    {isSuperAdmin && mode === 'create' && form.organizationid && (
                      <Box
                        sx={{
                          mt: 1,
                          p: 1.5,
                          borderRadius: '6px',
                          bgcolor: '#F8FAFF',
                          border: '1px solid #E2E8F0',
                        }}
                      >
                        {loadingSeatInfo ? (
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              color: '#6b7280',
                            }}
                          >
                            Loading seat information...
                          </Typography>
                        ) : selectedOrgSeatInfo ? (
                          <Box>
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: '#374151',
                                mb: 0.5,
                              }}
                            >
                              Seat Availability:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                              <Typography
                                sx={{
                                  fontSize: '0.75rem',
                                  color: '#6b7280',
                                }}
                              >
                                <strong>Total:</strong>{' '}
                                {selectedOrgSeatInfo.total_seats === 0
                                  ? 'Unlimited'
                                  : selectedOrgSeatInfo.total_seats}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.75rem',
                                  color: '#6b7280',
                                }}
                              >
                                <strong>Used:</strong> {selectedOrgSeatInfo.used_seats}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.75rem',
                                  color:
                                    selectedOrgSeatInfo.available_seats === -1
                                      ? '#6b7280'
                                      : selectedOrgSeatInfo.available_seats > 0
                                        ? '#2e7d32'
                                        : selectedOrgSeatInfo.available_seats === 0
                                          ? '#ed6c02'
                                          : '#d32f2f',
                                  fontWeight: 600,
                                }}
                              >
                                <strong>Available:</strong>{' '}
                                {selectedOrgSeatInfo.available_seats === -1
                                  ? 'Unlimited'
                                  : selectedOrgSeatInfo.available_seats}
                              </Typography>
                            </Box>
                            {selectedOrgSeatInfo.available_seats !== -1 &&
                              selectedOrgSeatInfo.available_seats === 0 && (
                                <Typography
                                  sx={{
                                    fontSize: '0.75rem',
                                    color: '#ed6c02',
                                    mt: 0.5,
                                    fontStyle: 'italic',
                                  }}
                                >
                                  ⚠️ No seats available. Cannot assign user to this organization.
                                </Typography>
                              )}
                          </Box>
                        ) : null}
                      </Box>
                    )}
                  </>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <StyledTextField
                  name="username"
                  label="Username"
                  value={form.username}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, username: e.target.value }))
                  }
                  variant="outlined"
                  fullWidth
                  disabled={mode === 'edit'}
                  error={!!errors.username}
                  helperText={errors.username || ' '}
                />
              </Grid>

              <Grid item xs={12}>
                <StyledTextField
                  type="email"
                  name="email"
                  label="Email"
                  value={form.email}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, email: e.target.value }))
                  }
                  variant="outlined"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email || ' '}
                />
              </Grid>

              {/* <Grid item xs={12} sm={6}>
                <StyledTextField
                  select
                  name="role"
                  label="Role"
                  value={form.role}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, role: e.target.value }))
                  }
                  variant="outlined"
                  fullWidth
                >
                  <MenuItem value="Admin">Admin</MenuItem>
                  <MenuItem value="User">User</MenuItem>
                </StyledTextField>
              </Grid> */}

              <Grid item xs={12} sm={6}>
                <StyledTextField
                  select
                  name="role"
                  label="Role"
                  value={isAdmin ? 'User' : form.role}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, role: e.target.value }))
                  }
                  variant="outlined"
                  fullWidth
                  disabled={isAdmin}
                >
                  <MenuItem value="Admin">Admin</MenuItem>
                  <MenuItem value="User">User</MenuItem>
                </StyledTextField>
              </Grid>

             {mode === 'edit' && ( <Grid item xs={12} sm={6}>
                <StyledTextField
                  select
                  name="status"
                  label="Status"
                  value={form.status}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, status: e.target.value }))
                  }
                  variant="outlined"
                  fullWidth
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </StyledTextField>
              </Grid>
              )}

              {mode === 'edit' && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={pwOpen}
                        onChange={(e) => {
                          setPwOpen(e.target.checked);
                          setErrors((prev) => ({
                            ...prev,
                            next: undefined,
                            confirm: undefined,
                          }));
                        }}
                      />
                    }
                    label="Change password"
                  />
                  {pwOpen ? (
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <StyledTextField
                          fullWidth
                          label="New Password"
                          type={show.next ? 'text' : 'password'}
                          value={pw.next}
                          onChange={(e) => {
                            const val = e.target.value;
                            setPw((s) => ({ ...s, next: val }));
                            if (pwOpen) validatePwEdit();
                          }}
                          onBlur={validatePwEdit}
                          error={!!errors.next}
                          helperText={errors.next || ' '}
                          InputProps={adorn('next')}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderRadius: 2,
                            },
                            '& .MuiOutlinedInput-root': {
                              height: 45,
                              alignItems: 'center',
                            },
                            '& .MuiInputAdornment-root': { m: 0 },
                            '& .MuiInputAdornment-root .MuiIconButton-root': {
                              width: 36,
                              height: 36,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <StyledTextField
                          fullWidth
                          label="Confirm New Password"
                          type={show.confirm ? 'text' : 'password'}
                          value={pw.confirm}
                          onChange={(e) => {
                            const val = e.target.value;
                            setPw((s) => ({ ...s, confirm: val }));
                            if (pwOpen) validatePwEdit();
                          }}
                          onBlur={validatePwEdit}
                          error={!!errors.confirm}
                          helperText={errors.confirm || ' '}
                          InputProps={adorn('confirm')}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderRadius: 2,
                            },
                            '& .MuiOutlinedInput-root': {
                              height: 45,
                              alignItems: 'center',
                            },
                            '& .MuiInputAdornment-root': { m: 0 },
                            '& .MuiInputAdornment-root .MuiIconButton-root': {
                              width: 36,
                              height: 36,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Need to change a password?
                    </Typography>
                  )}
                </Grid>
              )}

              {isSuperAdmin && mode === 'edit' && !form.organizationid && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#5965A6', fontWeight: 600 }}>
                    Trial Settings (Individual User)
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 2, color: '#6b7280' }}>
                    Note: Users in organizations inherit trial settings from their organization.
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        name="trial_days"
                        label="Trial Days"
                        type="number"
                        value={trialSettings.trial_days}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          if (value >= 1 || e.target.value === '') {
                            setTrialSettings((t) => ({
                              ...t,
                              trial_days: e.target.value === '' ? '' : value,
                            }));
                          }
                        }}
                        variant="outlined"
                        fullWidth
                        helperText="Default trial period in days"
                        InputProps={{
                          inputProps: { min: 1 },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        name="max_connectors"
                        label="Max Connectors"
                        type="number"
                        value={trialSettings.max_connectors}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          if (value >= 1 || e.target.value === '') {
                            setTrialSettings((t) => ({
                              ...t,
                              max_connectors: e.target.value === '' ? '' : value,
                            }));
                          }
                        }}
                        variant="outlined"
                        fullWidth
                        helperText="Maximum active connectors allowed"
                        InputProps={{
                          inputProps: { min: 1 },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={trialSettings.days_limit_enabled}
                            onChange={(e) =>
                              setTrialSettings((t) => ({
                                ...t,
                                days_limit_enabled: e.target.checked,
                              }))
                            }
                          />
                        }
                        label="Enable Days Limit"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={trialSettings.connectors_limit_enabled}
                            onChange={(e) =>
                              setTrialSettings((t) => ({
                                ...t,
                                connectors_limit_enabled: e.target.checked,
                              }))
                            }
                          />
                        }
                        label="Enable Connectors Limit"
                      />
                    </Grid>
                  </Grid>

                  {trialSettings.trial_end_date && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#F8FAFF', borderRadius: 1, border: '1px solid #E5E7EB' }}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                        Current Trial Status
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: '#6b7280', mb: 1 }}>
                        Trial End Date: {new Date(trialSettings.trial_end_date).toLocaleString()}
                      </Typography>
                      {trialSettings.trial_start_date && (
                        <Typography variant="caption" sx={{ display: 'block', color: '#6b7280', mb: 2 }}>
                          Trial Start Date: {new Date(trialSettings.trial_start_date).toLocaleString()}
                        </Typography>
                      )}
                      
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                        <TextField
                          size="small"
                          type="number"
                          label="Extend by (days)"
                          value={trialExtensionDays}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || (Number(val) >= 1 && Number(val) <= 365)) {
                              setTrialExtensionDays(val);
                            }
                          }}
                          sx={{ width: 150 }}
                          InputProps={{
                            inputProps: { min: 1, max: 365 },
                          }}
                          disabled={extendingTrial}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={async () => {
                            if (!trialExtensionDays || Number(trialExtensionDays) < 1) {
                              toast.error('Please enter a valid number of days (1-365)');
                              return;
                            }
                            
                            setExtendingTrial(true);
                            try {
                              await extendUserTrial(form.id, {
                                extension_days: Number(trialExtensionDays),
                              });
                              toast.success(`Trial extended by ${trialExtensionDays} days`);
                              
                              const fresh = await getUserById(form.id);
                              setTrialSettings((t) => ({
                                ...t,
                                trial_end_date: fresh.trial_end_date || t.trial_end_date,
                              }));
                              setTrialExtensionDays('');
                              onSubmit(fresh);
                            } catch (err) {
                              console.error('Failed to extend trial:', err);
                              toast.error(
                                err?.response?.data?.detail ||
                                  err?.response?.data?.message ||
                                  'Failed to extend trial'
                              );
                            } finally {
                              setExtendingTrial(false);
                            }
                          }}
                          disabled={extendingTrial || !trialExtensionDays}
                          sx={{ borderColor: '#5965A6', color: '#5965A6' }}
                        >
                          {extendingTrial ? 'Extending...' : 'Extend Trial'}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        
          <>
            <Button onClick={onClose} size="small" sx={{ color: '#5965A6' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={submit}
              sx={{ bgcolor: '#5965A6' }}
            >
              {cta}
            </Button>
          </>
        
      </DialogActions>
    </Dialog>
  );
}

UserDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['create', 'edit']),
  user: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
